import { useTheme } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Tag } from 'antd';
import { getRouteStackPath } from '@portal/config/routes';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { PixActions, SettingsActions } from '@portal/actions';
import { PixType } from '@portal/enum/pix';
import { translate } from '@portal/services/i18n';
import { PixStatus, PixStatusString } from '@portal/enum/PixStatus';
import { StorageItems, PIX_PORTABILITY_CACHE_TTL } from '~/services/storage';
import { maskCpfOrCnpj } from '@portal/services/masks';
import { WL_COMPANY_PRIMARY_COLOR } from '@portal/config/env';

import PixSkeleton from './PixSkeleton';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import useReduxState from '@portal/hooks/useReduxState';
import { usePixPortability } from '@portal/hooks/usePixPortability';
import { getEnumDescription } from '@portal/utils/enum';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import { useNavigation } from '@portal/hooks/useNavigation';

import { hasAccess } from '@portal/utils/hasAccess';
import RolesType from '@portal/enum/rolesType';
import CreateKey from '@portal/assets/svg/pix-buttons/ic_create-key.svg?react';
import TransferKey from '@portal/assets/svg/pix-buttons/ic_transfer.svg?react';
import PixCharge from '@portal/assets/svg/pix-buttons/ic_charge.svg?react';
import PixPay from '@portal/assets/svg/pix-buttons/ic_pix-pay.svg?react';
import WithdrawChange from '@portal/assets/svg/pix-buttons/ic_pix_withdraw_change.svg?react';
import PixLimit from '@portal/assets/svg/pix-buttons/ic_money-off.svg?react';
import { EventType } from '@portal/enum/analytics';
import { sendEvent } from '@portal/services/firebase';
import {
  pix,
  availableSoon,
  useColorInPixScreenIcons,
} from '@portal/config/app.json';
import AdvancedAlert from '@portal/components/AdvancedAlert/AdvancedAlert';
import MaintenanceModal from '@portal/components/MaintenanceModal/MaintenanceModal';
import {
  KeyPortabilityModal,
  ConfirmPortabilityModal,
  PendingPortabilityConfirmationModal,
  PixPortabilityModal,
} from '@portal/components/PixPortability';
import {
  AccountTaxIdentifierType,
  TransactionType,
} from '@portal/models/enumerators';
import { getLimitFlowPermissions } from '@portal/utils/transaction';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import MessageService from '@portal/services/message';
import AdvancedModal from '@portal/components/AdvancedModal/AdvancedModal';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedTooltip from '@portal/components/AdvancedTooltip/AdvancedTooltip';
import ThemedText from '@portal/components/ThemedText/ThemedText';

const Pix = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const { palette } = useTheme();
  const portability = usePixPortability();
  const {
    openPixPortabilityModal,
    setOpenPixPortabilityModal,
    pendingPortability,
    pendingPortabilityTagModalOpen,
    setPendingPortabilityTagModalOpen,
    pendingPortabilityKeyValue,
    setPendingPortabilityKeyValue,
    setPendingPortabilityStatus,
    confirmPortabilityModalOpen,
    setConfirmPortabilityModalOpen,
    openKeyPortabilityModal,
    setOpenKeyPortabilityModal,
    pixKey,
    setPixKey,
    validationModal,
    setValidationModal,
    handleConfirmTrue,
    handleConfirmFalse,
    handleValidationPin,
  } = portability;

  const {
    user: { roles, me },
    pix: {
      pixKeys,
      verifyCodeSuccess,
      pixDeleteSuccess,
      ownershipClaimSuccess,
    },
    loading: { load },
    settings: { remoteConfig },
    paymentAccount: { selectedPaymentAccount },
    theme: { currentTheme },
  } = useReduxState();

  const userId = me?.id ?? '';

  const isPortabilityConfirmed = useCallback(
    (key: string) => {
      if (!userId) return false;
      try {
        const data = JSON.parse(
          localStorage.getItem(StorageItems.PIX_PORTABILITY_CONFIRMED) ?? '{}'
        );
        const entry = data[`${userId}_${key}`];
        if (!entry) return false;
        if (Date.now() - entry > PIX_PORTABILITY_CACHE_TTL) {
          delete data[`${userId}_${key}`];
          localStorage.setItem(
            StorageItems.PIX_PORTABILITY_CONFIRMED,
            JSON.stringify(data)
          );
          return false;
        }
        return true;
      } catch {
        return false;
      }
    },
    [userId]
  );

  const markPortabilityConfirmed = useCallback(
    (key: string) => {
      if (!userId) return;
      try {
        const data = JSON.parse(
          localStorage.getItem(StorageItems.PIX_PORTABILITY_CONFIRMED) ?? '{}'
        );
        data[`${userId}_${key}`] = Date.now();
        localStorage.setItem(
          StorageItems.PIX_PORTABILITY_CONFIRMED,
          JSON.stringify(data)
        );
      } catch {
        /* noop */
      }
    },
    [userId]
  );

  const [firstPixFetch, setFirstPixFetch] = useState<boolean>(true);
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);
  const [updatedAttempts, setUpdatedAttempts] = useState(0);
  const [openMessageModal, setOpenMessageModal] = useState(false);
  const [giveawayConfirmModalOpen, setGiveawayConfirmModalOpen] =
    useState(false);
  const [giveawayConfirmKey, setGiveawayConfirmKey] =
    useState<models.RegisterPixKeyDetails | null>(null);

  const handlePixLabel = (value: PixType) => {
    if (PixType.EMAIL === value) {
      return translate('PAGES.HOME.PIX.LABELS.EMAIL');
    }
    if (PixType.PHONE === value) {
      return translate('PAGES.HOME.PIX.LABELS.PHONE');
    }
    if (PixType.TAX_IDENTIFIER === value) {
      return translate('PAGES.HOME.PIX.LABELS.TAX_IDENTIFIER');
    }
    if (PixType.RANDOM === value) {
      return translate('PAGES.HOME.PIX.LABELS.RANDOM');
    }
  };

  const openMaintenance = () => {
    setOpenMaintenanceModal(true);
  };

  useEffect(() => {
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }
  }, [dispatch, remoteConfig]);

  const removeCountryCode = (value: string) =>
    value.replace('+55', '').replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');

  const renderPixStatusName = (
    status: PixStatus,
    pixKeyItem: models.RegisterPixKeyDetails
  ) => {
    const isGiveawayConfirm =
      pixKeyItem.statusString ===
        PixStatusString.IM_OWNER_PENDING_GIVEAWAY_CONFIRM &&
      pixKeyItem.isReivindication === true;

    const isClaimerPending =
      pixKeyItem.statusString ===
        PixStatusString.IM_CLAIMER_PENDING_OWNER_GIVEWAY &&
      pixKeyItem.isReivindication === true;

    if (isClaimerPending) {
      if (isPortabilityConfirmed(pixKeyItem.key)) {
        return (
          <Tag className="pix__body__tag pix__body__tag--portability pix__body__tag--portability-processing">
            {translate(
              'PAGES.HOME.PIX.STATUS.IM_CLAIMER_PENDING_OWNER_GIVEWAY'
            )}
          </Tag>
        );
      }
      return (
        <Tag
          className="pix__body__tag pix__body__tag--portability"
          onClick={() => {
            setPixKey(pixKeyItem.key);
            setPendingPortabilityKeyValue(pixKeyItem.key);
            setPendingPortabilityStatus(status);
            setPendingPortabilityTagModalOpen(true);
          }}
        >
          {translate('PAGES.HOME.PIX.STATUS.IM_CLAIMER_PENDING_OWNER_GIVEWAY')}
        </Tag>
      );
    }

    if (isGiveawayConfirm) {
      if (isPortabilityConfirmed(pixKeyItem.key)) {
        return (
          <Tag className="pix__body__tag pix__body__tag--claim pix__body__tag--portability-processing">
            {getEnumDescription(status, 'STATUS_VALUES')}
          </Tag>
        );
      }
      return (
        <Tag
          className="pix__body__tag pix__body__tag--claim"
          onClick={() => {
            setGiveawayConfirmKey(pixKeyItem);
            setGiveawayConfirmModalOpen(true);
          }}
        >
          {translate('PAGES.HOME.PIX.STATUS.IM_OWNER_PENDING_GIVEAWAY_CONFIRM')}
        </Tag>
      );
    }

    const isProcessingPortability =
      status === PixStatus.PENDING_VALIDATION_COMPLETE_CLAIM ||
      status === PixStatus.IM_CLAIMER_AND_OWNER_GAVEAWAY_PENDING_MY_CONFIRM;

    if (isProcessingPortability) {
      const tooltipText = pixKeyItem.limitDate
        ? `${translate(
            'PAGES.HOME.PIX.PORTABILITY_PROCESSING_TOOLTIP'
          )} ${translate('PAGES.HOME.PIX.LIMIT_DATE', {
            date: new Date(pixKeyItem.limitDate).toLocaleDateString('pt-BR'),
          })}`
        : translate('PAGES.HOME.PIX.PORTABILITY_PROCESSING_TOOLTIP');
      return (
        <AdvancedTooltip title={tooltipText}>
          <span style={{ cursor: 'default' }}>
            <Tag className="pix__body__tag pix__body__tag--portability pix__body__tag--portability-processing">
              {getEnumDescription(status, 'STATUS_VALUES')}
            </Tag>
          </span>
        </AdvancedTooltip>
      );
    }

    if (
      status === PixStatus.PENDING_PORTABILITY_CONFIRMATION ||
      status === PixStatus.USER_CONFIRMATION_PENDING_PORTABILITY ||
      status === PixStatus.CLAIM_AWAITING_RETURN_PSP_DONOR
    ) {
      if (isPortabilityConfirmed(pixKeyItem.key)) {
        return (
          <Tag className="pix__body__tag pix__body__tag--portability pix__body__tag--portability-processing">
            {getEnumDescription(status, 'STATUS_VALUES')}
          </Tag>
        );
      }
      return (
        <Tag
          className="pix__body__tag pix__body__tag--portability"
          onClick={() => {
            setPixKey(pixKeyItem.key);
            setPendingPortabilityKeyValue(pixKeyItem.key);
            setPendingPortabilityStatus(status);
            setPendingPortabilityTagModalOpen(true);
          }}
        >
          {getEnumDescription(status, 'STATUS_VALUES')}
        </Tag>
      );
    }
  };

  const renderPixKey = (value: string, type: number) => {
    if (type === PixType.PHONE) return removeCountryCode(value);
    else if (type === PixType.TAX_IDENTIFIER) return maskCpfOrCnpj(value);
    return value;
  };

  const deletePixKey = (key: string) => {
    dispatch(PixActions.deletePixKey(key));
  };

  const handleCopyPixKey = (key: string) => {
    navigator.clipboard.writeText(key);
    MessageService.success(
      translate('PAGES.HOME.PIX.OPTIONS_MENU.COPY_PIX_KEY_SUCCESS')
    );
  };

  const menuOptions = (key: string) => {
    const options = [
      {
        key: 'copy',
        icon: <CopyOutlined style={{ color: WL_COMPANY_PRIMARY_COLOR }} />,
        onClick: () => handleCopyPixKey(key),
        className: 'pix__keys__item__buttons__copy',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined style={{ color: WL_COMPANY_PRIMARY_COLOR }} />,
        onClick: () => deletePixKey(key),
        className: 'pix__keys__item__buttons__delete',
      },
    ];
    return options.map((option) => (
      <button
        className={option.className}
        key={option.key}
        onClick={option.onClick}
      >
        {option.icon}
      </button>
    ));
  };

  useEffect(() => {
    if (!pixDeleteSuccess) return;

    dispatch(PixActions.getAllPixKeys());
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [pixDeleteSuccess]);

  useEffect(() => {
    const hasPixAccess = hasAccess(roles, [
      RolesType.PIX_MANAGE_KEYS,
      RolesType.PIX_CONTROL_KEYS,
    ]);
    if (!hasPixAccess || !pix.enabled) return;

    if (!firstPixFetch) {
      dispatch(PixActions.getAllPixKeys());
    }
    dispatch(PixActions.cleanRegisterPixKey());
  }, []);

  useEffect(() => {
    if (!ownershipClaimSuccess) return;

    navigate(getRouteStackPath('PIX', 'PIX'));
  }, [ownershipClaimSuccess]);

  useEffect(() => {
    if (!verifyCodeSuccess) return;

    dispatch(PixActions.getAllPixKeys());
  }, [verifyCodeSuccess]);

  useEffect(() => {
    if (!pix.enabled || availableSoon.pix) return;

    setFirstPixFetch(false);
    dispatch(PixActions.getAllPixKeys());
  }, [selectedPaymentAccount?.id]);

  const hasPendingKey = useMemo(() => {
    if (!pixKeys) return false;

    return pixKeys.some(
      (item) => item.status === PixStatus.CLEARING_REGISTRATION_PENDING
    );
  }, [pixKeys]);

  const updatePixKeys = useCallback(() => {
    setTimeout(() => {
      if (updatedAttempts >= 2) return;

      dispatch(PixActions.getAllPixKeys());
      setUpdatedAttempts((prevState) => prevState + 1);
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasPendingKey) return;

    updatePixKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPendingKey]);

  const maxPixKeysValidation = () =>
    (me.account.type === AccountTaxIdentifierType.PERSON &&
      pixKeys?.length === 5) ||
    (me.account.type === AccountTaxIdentifierType.CORPORATE &&
      pixKeys?.length === 20);

  const pixActions = () => {
    const actions = [
      {
        hasAccess: hasAccess(roles, [
          RolesType.PIX_MANAGE_KEYS,
          RolesType.PIX_CONTROL_KEYS,
        ]),
        text: translate('PAGES.HOME.PIX.BUTTONS.CREATE_KEY'),
        icon: <CreateKey />,
        link: getRouteStackPath('PIX', 'KEYS'),
        onClick: () => sendEvent(EventType.PIX_CREATE_KEY_BUTTON),
        description: translate('PAGES.HOME.PIX.DESCRIPTIONS.CREATE_KEY'),
        disable: maxPixKeysValidation(),
      },
      {
        hasAccess: hasAccess(roles, [
          RolesType.TRANSACTION_CREATE,
          RolesType.PIX_CREATE_TRANSACTION,
        ]),
        text: translate('PAGES.HOME.PIX.BUTTONS.TRANSFER'),
        icon: (
          <TransferKey
            fill={
              useColorInPixScreenIcons.useSecondaryColor
                ? palette.secondary.main
                : palette.primary.main
            }
            width={30}
            height={30}
          />
        ),
        link: remoteConfig?.disableCashOut
          ? undefined
          : getRouteStackPath('PIX', 'TRANSFER'),
        onClick: remoteConfig?.disableCashOut
          ? () => openMaintenance()
          : () => sendEvent(EventType.BOTTOM_MODAL_TRANSFER_PIX),
        description: translate('PAGES.HOME.PIX.DESCRIPTIONS.TRANSFER'),
      },
      {
        hasAccess:
          pix.enableCopyPaste &&
          hasAccess(roles, [
            RolesType.TRANSACTION_CREATE,
            RolesType.PIX_CREATE_TRANSACTION,
          ]),
        text: translate('PAGES.HOME.PIX.BUTTONS.COPY_PASTE'),
        icon: <PixPay />,
        link: remoteConfig?.disableCashOut
          ? undefined
          : getRouteStackPath('PIX', 'COPY_PASTE'),
        onClick: remoteConfig?.disableCashOut
          ? () => openMaintenance()
          : () => sendEvent(EventType.PIX_PAY_BUTTON),
        description: translate('PAGES.HOME.PIX.DESCRIPTIONS.COPY_PASTE'),
      },
      {
        hasAccess:
          pix.pixWithdrawAndChange &&
          hasAccess(roles, [
            RolesType.TRANSACTION_CREATE,
            RolesType.PIX_CREATE_TRANSACTION,
          ]),
        text: translate('PAGES.HOME.PIX.BUTTONS.WITHDRAW_AND_CHARGE'),
        icon: <WithdrawChange />,
        link: remoteConfig?.disableCashIn
          ? undefined
          : getRouteStackPath('PIX', 'WITHDRAW_AND_CHANGE'),
        onClick: remoteConfig?.disableCashIn
          ? () => openMaintenance()
          : () => sendEvent(EventType.PIX_PAY_BUTTON),
        description: translate(
          'PAGES.HOME.PIX.DESCRIPTIONS.WITHDRAW_AND_CHARGE'
        ),
      },
      {
        hasAccess:
          pix.enableRequest &&
          hasAccess(roles, [
            RolesType.TRANSACTION_CREATE,
            RolesType.PIX_CREATE_TRANSACTION,
          ]),
        text: translate('PAGES.HOME.PIX.BUTTONS.REQUEST'),
        icon: <PixCharge />,
        linkData: 'pix-charge',
        link: remoteConfig?.disableCashIn
          ? undefined
          : getRouteStackPath('HOME', 'RECEBER'),
        onClick: remoteConfig?.disableCashIn
          ? () => openMaintenance()
          : () => sendEvent(EventType.PIX_REQUEST_BUTTON),
        description: translate('PAGES.HOME.PIX.DESCRIPTIONS.REQUEST'),
      },
      {
        hasAccess: getLimitFlowPermissions(TransactionType.PIX).read,
        text: translate('PAGES.HOME.PIX.BUTTONS.LIMITS'),
        icon: <PixLimit />,
        link: getRouteStackPath('LIMITS', 'LIMITS_PIX'),
        onClick: () => sendEvent(EventType.PIX_LIMITS_BUTTON),
        description: translate('PAGES.HOME.PIX.DESCRIPTIONS.LIMITS'),
      },
    ];

    return actions
      .filter((action) => action.hasAccess)
      .map((action) => (
        <button
          className="pix__card"
          key={action.text}
          onClick={() => {
            if (action.disable) {
              setOpenMessageModal(true);
              return;
            }

            if (action.link) {
              navigate(action.link, { state: action.linkData });
            }
            if (action.onClick) {
              action.onClick();
            }
          }}
        >
          <div
            className="pix__card__icon"
            style={{ background: currentTheme.background }}
          >
            {action.icon}
          </div>
          <ThemedText variant="h3" className="pix__card__title">
            {action.text}
          </ThemedText>
          <ThemedText variant="body" className="pix__card__description">
            {action.description}
          </ThemedText>
        </button>
      ));
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        title={translate('APPLICATION.TITLE')}
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: false,
            title: translate('PAGES.HOME.PIX.PAGE_TITLE'),
          },
        ]}
      />
      <div className="pix">
        <AdvancedPanelContentHeader
          testId="pix-header"
          title={translate('PAGES.HOME.PIX.PAGE_TITLE')}
          description={translate('PAGES.HOME.PIX.PAGE_DESCRIPTION')}
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
          buttonTitle={translate('PAGES.HOME.PIX.BUTTONS.BACK_INIT')}
        />
        {!availableSoon.pix ? (
          <div className="pix__grid">{pixActions()}</div>
        ) : (
          <AdvancedAlert>
            {translate('PAGES.HOME.PIX.AVAILABLE_SOON')}
          </AdvancedAlert>
        )}
        {hasAccess(roles, [
          RolesType.PIX_MANAGE_KEYS,
          RolesType.PIX_CONTROL_KEYS,
        ]) && (
          <div className="pix__keys">
            <div className="pix__keys__header">
              <ThemedText variant="body" className="pix__body__header">
                {translate('PAGES.HOME.PIX.TEXT.MY_KEYS')}
              </ThemedText>
              <AdvancedButton
                text={translate('PAGES.HOME.PIX.BUTTONS.REFRESH')}
                variant="outlined"
                onClick={() => dispatch(PixActions.getAllPixKeys())}
                size="small"
                className="refresh-text"
              />
            </div>
            {pixKeys?.length && !load ? (
              <>
                {pixKeys.map((pixKey) => (
                  <div className="pix__keys__item" key={pixKey.key}>
                    <div className="pix__keys__item__key">
                      <ThemedText variant="body" className="pix__body__label">
                        {handlePixLabel(pixKey.type)}
                      </ThemedText>
                      <ThemedText variant="body" className="pix__body__text">
                        {renderPixKey(pixKey.key, pixKey.type)}
                      </ThemedText>
                    </div>
                    <div className="pix__keys__item__actions">
                      {renderPixStatusName(pixKey.status, pixKey)}
                      {(pixKey.status === PixStatus.ACTIVE ||
                        pixKey.status ===
                          PixStatus.IDENTITY_VALIDATION_PENDING) && (
                        <div className="pix__keys__item__buttons">
                          {menuOptions(pixKey.key)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : load ? (
              <PixSkeleton />
            ) : (
              <AdvancedAlert>
                {translate('PAGES.HOME.PIX.NO_KEYS')}
              </AdvancedAlert>
            )}
          </div>
        )}
      </div>
      <ConfirmationModal
        visible={validationModal}
        setVisible={setValidationModal}
        handleConfirmation={handleValidationPin}
      />
      <MaintenanceModal
        setVisible={setOpenMaintenanceModal}
        visible={openMaintenanceModal}
      />
      <KeyPortabilityModal
        visible={openKeyPortabilityModal}
        setVisible={setOpenKeyPortabilityModal}
        pixKey={pixKey}
        buttonOk={handleConfirmTrue}
      />
      <AdvancedModal
        title={translate('PAGES.HOME.PIX.BUTTONS.CREATE_KEY')}
        visible={openMessageModal}
        size="small"
        onClose={() => setOpenMessageModal(false)}
        autoHeight
      >
        {translate('PAGES.HOME.PIX.ERROR.PIX_MAX_KEYS')}
      </AdvancedModal>

      <ConfirmPortabilityModal
        visible={confirmPortabilityModalOpen}
        setVisible={setConfirmPortabilityModalOpen}
        onConfirm={() => {
          dispatch(PixActions.portabilityResponse(pixKey, true));
          setConfirmPortabilityModalOpen(false);
        }}
        onRefuse={() => {
          dispatch(PixActions.deletePortability(pixKey));
          setConfirmPortabilityModalOpen(false);
        }}
        loading={load}
      />
      <PendingPortabilityConfirmationModal
        visible={pendingPortabilityTagModalOpen}
        setVisible={setPendingPortabilityTagModalOpen}
        keyValue={pendingPortabilityKeyValue}
        limitDate={
          pixKeys?.find((k) => k.key === pendingPortabilityKeyValue)?.limitDate
        }
        onClose={() => {
          setPendingPortabilityTagModalOpen(false);
          setPendingPortabilityKeyValue('');
          setPendingPortabilityStatus(undefined);
        }}
        onCancelPortability={() => {
          dispatch(PixActions.deletePortability(pendingPortabilityKeyValue));
          markPortabilityConfirmed(pendingPortabilityKeyValue);
          setPendingPortabilityTagModalOpen(false);
          setPendingPortabilityKeyValue('');
          setPendingPortabilityStatus(undefined);
        }}
      />
      <PixPortabilityModal
        visible={openPixPortabilityModal}
        setVisible={setOpenPixPortabilityModal}
        pendingPortability={pendingPortability}
        onKeepKey={() => {
          handleConfirmTrue();
          if (pendingPortability?.key)
            markPortabilityConfirmed(pendingPortability.key);
        }}
        onUseInOtherAccount={() => {
          handleConfirmFalse();
          if (pendingPortability?.key)
            markPortabilityConfirmed(pendingPortability.key);
        }}
      />
      <PixPortabilityModal
        visible={giveawayConfirmModalOpen}
        setVisible={setGiveawayConfirmModalOpen}
        pendingPortability={
          giveawayConfirmKey
            ? {
                key: giveawayConfirmKey.key,
                type: giveawayConfirmKey.type,
                isReivindication: giveawayConfirmKey.isReivindication,
                dueDate: giveawayConfirmKey.limitDate,
              }
            : null
        }
        onKeepKey={() => {
          dispatch(PixActions.deletePortability(giveawayConfirmKey?.key ?? ''));
          markPortabilityConfirmed(giveawayConfirmKey?.key ?? '');
          setGiveawayConfirmModalOpen(false);
          setGiveawayConfirmKey(null);
        }}
        onUseInOtherAccount={() => {
          dispatch(
            PixActions.portabilityConfirmSetup(
              giveawayConfirmKey?.key ?? '',
              true
            )
          );
          markPortabilityConfirmed(giveawayConfirmKey?.key ?? '');
          setGiveawayConfirmModalOpen(false);
          setGiveawayConfirmKey(null);
        }}
      />
    </>
  );
};

export default Pix;
